import { Controller } from "@hotwired/stimulus"

// Legend toggling plus a month-snapped crosshair and tooltip for the financials
// line chart. All values shown here are also in the ledger table below the chart.
export default class extends Controller {
  static targets = ["svg", "crosshair", "tooltip", "series", "dot"]
  static values = { data: Object }

  disconnect() {
    this.hide()
  }

  toggle(event) {
    const button = event.currentTarget
    const key = button.dataset.seriesKey
    const pressed = button.getAttribute("aria-pressed") === "true"

    button.setAttribute("aria-pressed", String(!pressed))
    this.seriesGroup(key)?.classList.toggle("hidden", pressed)
    if (this.activeIndex != null) this.show(this.activeIndex)
  }

  move(event) {
    const rect = this.svgTarget.getBoundingClientRect()
    const x = ((event.clientX - rect.left) / rect.width) * this.viewBoxWidth()
    this.show(this.nearestIndex(x))
  }

  keydown(event) {
    if (event.key === "Escape") return this.hide()
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return

    event.preventDefault()
    const step = event.key === "ArrowRight" ? 1 : -1
    const index = this.activeIndex == null ? 0 : this.activeIndex + step
    this.show(Math.max(0, Math.min(11, index)))
  }

  hide() {
    this.activeIndex = null
    this.crosshairTarget.classList.add("hidden")
    this.tooltipTarget.classList.add("hidden")
    this.dotTargets.forEach((dot) => dot.classList.add("hidden"))
  }

  show(index) {
    this.activeIndex = index
    const month = this.dataValue.months[index]
    const x = this.dataValue.xs[index]

    this.crosshairTarget.setAttribute("x1", x)
    this.crosshairTarget.setAttribute("x2", x)
    this.crosshairTarget.classList.remove("hidden")

    this.dotTargets.forEach((dot) => {
      const series = month.series.find((entry) => entry.key === dot.dataset.seriesKey)
      dot.setAttribute("cx", x)
      dot.setAttribute("cy", series.y)
      dot.classList.remove("hidden")
    })

    this.renderTooltip(month)
    this.positionTooltip(x)
  }

  renderTooltip(month) {
    const tooltip = this.tooltipTarget
    tooltip.replaceChildren()

    const title = document.createElement("div")
    title.className = "mb-1 font-medium text-neutral-900"
    title.textContent = month.label
    tooltip.append(title)

    month.series.forEach((entry) => {
      if (this.seriesGroup(entry.key)?.classList.contains("hidden")) return

      const row = document.createElement("div")
      row.className = "flex items-center gap-2 py-0.5"

      const key = document.createElement("span")
      key.className = "h-0.5 w-3 shrink-0 rounded-full"
      key.style.backgroundColor = entry.color

      const value = document.createElement("span")
      value.className = "font-semibold tabular-nums text-neutral-900"
      value.textContent = entry.value

      const label = document.createElement("span")
      label.className = "text-neutral-500"
      label.textContent = entry.estimated ? `${entry.label} (est.)` : entry.label

      row.append(key, value, label)
      tooltip.append(row)
    })

    tooltip.classList.remove("hidden")
  }

  positionTooltip(x) {
    const rect = this.svgTarget.getBoundingClientRect()
    const pixelX = (x / this.viewBoxWidth()) * rect.width

    if (x > this.viewBoxWidth() / 2) {
      this.tooltipTarget.style.left = `${pixelX - this.tooltipTarget.offsetWidth - 12}px`
    } else {
      this.tooltipTarget.style.left = `${pixelX + 12}px`
    }
  }

  nearestIndex(x) {
    return this.dataValue.xs.reduce(
      (nearest, candidate, index) =>
        Math.abs(candidate - x) < Math.abs(this.dataValue.xs[nearest] - x) ? index : nearest,
      0
    )
  }

  seriesGroup(key) {
    return this.seriesTargets.find((group) => group.dataset.seriesKey === key)
  }

  viewBoxWidth() {
    return this.svgTarget.viewBox.baseVal.width
  }
}
